import type { CourseListItem } from '@/features/courses/useCourses';

export interface CopyTargetCourseOption {
  id: string;
  isCurrent: boolean;
  label: string;
}

interface GetCopyTargetCourseOptionsInput {
  courses: CourseListItem[];
  currentCourseId: string;
  currentCourseTitle?: string | null;
  userCourseIds: string[];
}

function createOption(
  courseId: string,
  courseById: Map<string, CourseListItem>,
  currentCourseId: string,
  currentCourseTitle?: string | null,
): CopyTargetCourseOption {
  const course = courseById.get(courseId);
  const fallbackTitle =
    courseId === currentCourseId ? currentCourseTitle?.trim() : null;

  return {
    id: courseId,
    isCurrent: courseId === currentCourseId,
    label: course?.title?.trim() || fallbackTitle || courseId,
  };
}

export function getCopyTargetCourseOptions({
  courses,
  currentCourseId,
  currentCourseTitle,
  userCourseIds,
}: GetCopyTargetCourseOptionsInput): CopyTargetCourseOption[] {
  const instructorCourses = courses.filter(
    (course) => course.id === currentCourseId || course.userRole !== 'student' && course.userRole !== 'assistant',
  );
  const courseById = new Map(instructorCourses.map((course) => [course.id, course]));
  const orderedIds: string[] = [];
  const seenIds = new Set<string>();

  const addCourseId = (courseId: string | null | undefined) => {
    const normalized = courseId?.trim();

    if (!normalized || seenIds.has(normalized)) {
      return;
    }

    orderedIds.push(normalized);
    seenIds.add(normalized);
  };

  addCourseId(currentCourseId);

  for (const courseId of [...userCourseIds].reverse()) {
    if (courseId !== currentCourseId) {
      addCourseId(courseId);
    }
  }

  for (const course of instructorCourses) {
    addCourseId(course.id);
  }

  return orderedIds
    .filter((courseId) => courseId === currentCourseId || courseById.has(courseId))
    .map((courseId) =>
      createOption(courseId, courseById, currentCourseId, currentCourseTitle),
    );
}
